import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  SUB_CONSULT_MODE,
  SUB_CREATE_MODE,
  SUB_UPDATE_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  PREPARING_ACTION_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  activateById,
  fetchAll,
  fetchByFilter,
  fetchById,
  inactivateById,
} from './PortoAeroportoAPI';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],
  model: {
    code: null,
    description: null,
    insertionDate: null,
    active: null,
    sigla: null,
    pais: { id: null },
    local: null,
  },
  porLocal: {},          
  statusPorLocal: {},    
  erroPorLocal: {},
};

const fetchAllAsync = createAsyncThunk(
  'portoAeroporto/fetchAll',
  async () => {
    const { data } = await fetchAll();
    return { data };
  }
);

const fetchByIdAsync = createAsyncThunk(
  'portoAeroporto/fetchById',
  async (data) => {
    const response = await fetchById(data);
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

 const fetchByFilterAsync = createAsyncThunk(
  'portoAeroporto/fetchByFilter',
  async (params) => {
    const { data } = await fetchByFilter(params);
    return { data, params };
  },
  {
    condition: (params, { getState }) => {
      const local = getLocalFromArg(params);
      const st = getState().portoAeroporto;

      // Já carregando? Bloqueia
      if (st?.statusPorLocal?.[local] === 'loading') return false;

      // Já temos dados para esse local? Bloqueia
      if (Array.isArray(st?.porLocal?.[local]) && st.porLocal[local].length > 0) return false;

      return true; // ok fazer fetch
    },
  }
);

const getLocalFromArg = (arg) => {
  if (!Array.isArray(arg)) return 'ALL';
  const found = arg.find((p) => p.name === 'local');
  return found?.value || 'ALL';
};

const activateByIdAsync = createAsyncThunk(
  'portoAeroporto/activateById',
  async (id) => {
    const { status, data } = await activateById(id);
    return { response: { status, data } };
  }
);

const inactivateByIdAsync = createAsyncThunk(
  'portoAeroporto/inactivateById',
  async (id) => {
    const { status, data } = await inactivateById(id);
    return { response: { status, data } };
  }
);

const portoAeroportoSlice = createSlice({
  name: 'portoAeroporto',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    changeToSubCreateMode: (state) => {
      state.mode.sub = SUB_CREATE_MODE;
    },
    changeToSubConsultMode: (state) => {
      state.mode.sub = SUB_CONSULT_MODE;
    },
    changeToSubUpdateMode: (state) => {
      state.mode.sub = SUB_UPDATE_MODE;
    },
    resetSubMode: (state) => {
      state.mode.sub = NONE_SUB_MODE;
    },
    setModel: (state, action) => {
      state.model = {
        ...action.payload,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    resetList: (state) => {
      state.list = [];
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = initialState.error;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
        // <<< ADIÇÃO ESTRITAMENTE NECESSÁRIA PARA "SEM FILTRO = TUDO"
        state.porLocal.ALL = action.payload.data || [];
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaPortoAeroporto', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = {
            ...action.payload.response.data,
          };
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarPortoAeroporto', { erro: action.error.message }),
        };
      })
      .addCase(fetchByFilterAsync.pending, (state, action) => {
        const local = getLocalFromArg(action.meta.arg);
        state.statusPorLocal[local] = 'loading';
        state.erroPorLocal[local] = undefined;
      })
      .addCase(fetchByFilterAsync.fulfilled, (state, action) => {
        const local = getLocalFromArg(action.meta.arg);
        state.porLocal[local] = action.payload?.data || [];
        state.statusPorLocal[local] = 'succeeded';
      })
      .addCase(fetchByFilterAsync.rejected, (state, action) => {
        const local = getLocalFromArg(action.meta.arg);
        state.statusPorLocal[local] = 'failed';
        state.erroPorLocal[local] = action.error?.message;
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const description = action.payload.response.data?.description;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.portoAeroportoAtivado', { portoAeroporto: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarPortoAeroporto', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const description = action.payload.response.data?.description;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.portoAeroportoInativado', { portoAeroporto: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarPortoAeroporto', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  changeToSubCreateMode,
  resetSubMode,
  setModel,
  resetModel,
  addToList,
  updateOnList,
  resetList,
  setResponse,
  clearResponse,
  setError,
  clearError,
} = portoAeroportoSlice.actions;

const selectStatus = (state) => state.portoAeroporto.status;
const selectMode = (state) => state.portoAeroporto.mode.main;
const selectBackgroundMode = (state) => state.portoAeroporto.mode.background;
const selectSubMode = (state) => state.portoAeroporto.mode.sub;
const selectPortosAeroportos = (state) => state.portoAeroporto.list;
const selectPortosAeroportosLocalOrigem = (state) => state.portoAeroporto.list.filter((v) => v?.local === "LOCAL_DE_ORIGEM");
const selectPortosAeroportosLocalDestino = (state) => state.portoAeroporto.list.filter((v) => v?.local === "LOCAL_DE_DESTINO");
const selectPortoAeroporto = (state) => state.portoAeroporto.model;
const selectError = (state) => state.portoAeroporto.error;
const selectResponse = (state) => state.portoAeroporto.response;

export const selectPortosAeroportosByLocal = (state, local) =>
  (state.portoAeroporto?.porLocal?.[local] || []);

export const selectStatusPorLocal = (state, local) =>
  (state.portoAeroporto?.statusPorLocal?.[local] || 'idle');

export const selectErroPorLocal = (state, local) =>
  (state.portoAeroporto?.erroPorLocal?.[local]);

export {
  portoAeroportoSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  changeToSubCreateMode,
  resetSubMode,
  setModel,
  resetModel,
  addToList,
  updateOnList,
  resetList,
  setResponse,
  clearResponse,
  setError,
  clearError,
  fetchAllAsync,
  fetchByIdAsync,
  fetchByFilterAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  selectStatus,
  selectMode,
  selectBackgroundMode,
  selectSubMode,
  selectPortoAeroporto,
  selectPortosAeroportos,
  selectPortosAeroportosLocalOrigem,
  selectPortosAeroportosLocalDestino,
  selectError,
  selectResponse,
};

export default portoAeroportoSlice.reducer;
